import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { logger } from './logger';
import type { ApiError } from './axios';

/**
 * Type guard to check if error is a normalized ApiError
 */
function isApiError(error: unknown): error is ApiError {
  return error instanceof Error && 'isNetworkError' in error;
}

/**
 * Check if error is a global error (401/429) that's already handled by axios interceptors
 */
export const isGlobalError = (error: unknown): boolean => {
  if (isApiError(error)) {
    return error.status === 401 || error.status === 429;
  }
  return false;
};

/**
 * Handle API errors with proper severity levels
 * - Network errors (backend down) → Sentry (alerts) + Logger
 * - 5xx: Server errors → Sentry (alerts) + Logger (dashboards)
 * - 4xx: Client errors → Logger only (informational)
 * - 401/429: Skipped (handled by axios interceptor)
 */
function handleApiError(error: unknown) {
  // Skip 401/429 - already handled by axios interceptor
  if (isGlobalError(error)) return;

  // Use normalized ApiError properties if available
  const apiError = isApiError(error) ? error : null;
  const status = apiError?.status;
  const url = apiError?.url;
  const method = apiError?.method;
  const code = apiError?.code;
  const isNetworkError = apiError?.isNetworkError ?? false;

  if (isNetworkError) {
    // Network error - backend is down, CORS issue, or connection refused
    Sentry.captureException(error, {
      extra: { url, method, code },
      tags: { errorType: 'network_error' },
    });
    logger.error('Network error - backend unreachable', error as Error, {
      url,
      method,
      code,
    });
  } else if (status && status >= 500) {
    // Server errors - send to BOTH Sentry (alerts) and Logger (dashboards)
    Sentry.captureException(error, {
      extra: { status, url, method },
      tags: { errorType: 'api_5xx' },
    });
    logger.error('API server error', error as Error, { status, url, method });
  } else if (status && status >= 400) {
    // Client errors (4xx) - just log as warning
    logger.warn('API client error', { status, url, method });
  }
}

/**
 * QueryClient with global error handling
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: (failureCount, error) => {
        // Don't retry on auth or rate limit errors
        if (isGlobalError(error)) {
          return false;
        }
        return failureCount < 3;
      },
    },
    mutations: {
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => handleApiError(error),
  }),
  mutationCache: new MutationCache({
    onError: (error) => handleApiError(error),
  }),
});
